import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import ResizableSplitPane from "../../../ui/layout/ResizableSplitPane";
import type { EntityLookup } from "./detailParts";
import OntologyEntityDetails from "./OntologyEntityDetails";
import OntologyHierarchyTree from "./OntologyHierarchyTree";
import OntologyRelationDetails from "./OntologyRelationDetails";
import OntologyRelationTree from "./OntologyRelationTree";
import {
  buildOntologyEntityIndexes,
  buildOntologyRelations,
  groupRelationsBySource,
  groupRelationsByTarget,
} from "./relationTree";
import type { OntologyDisplayMode, OntologyEntity, OntologyEntityType, OntologyViewerModel, OntologyViewerPane } from "./types";
import { entityDisplayName, isOntologyRootIri, ONTOLOGY_VIEWER_PANES, topLevelEntities } from "./types";
import SegmentedControl from "../../../ui/navigation/SegmentedControl";

interface OntologyViewerProps {
  model: OntologyViewerModel;
  /** The raw view: the Turtle of a module. */
  rawPanel: ReactNode;
  displayMode: OntologyDisplayMode;
  entityType: OntologyViewerPane;
  selectedEntityIri: string | null;
  storageScope: string;
  onDisplayModeChange: (mode: OntologyDisplayMode) => void;
  onEntityTypeChange: (entityType: OntologyViewerPane) => void;
  onSelectEntity: (iri: string, entityType?: OntologyEntityType) => void;
  /** The details of a term of the version (every entry but the roots of the trees). */
  renderTermDetails: (entity: OntologyEntity, lookup: EntityLookup) => ReactNode;
  /** Terms a failing check names, marked in the trees. */
  failingIris: ReadonlySet<string> | null;
}

const ENTITY_TYPE_LABEL_KEYS: Record<OntologyViewerPane, string> = {
  classes: "ontologyViewer.entityTypes.classes",
  objectProperties: "ontologyViewer.entityTypes.objectProperties",
  dataProperties: "ontologyViewer.entityTypes.dataProperties",
  annotationProperties: "ontologyViewer.entityTypes.annotationProperties",
  individuals: "ontologyViewer.entityTypes.individuals",
  relations: "ontologyViewer.entityTypes.relations",
};

/** The ontology structured (one tree per kind of term, or the relation map, beside the
 * selected entry's details, in a resizable split that fills the page, as v1's; F77) or raw
 * (Turtle). The kinds are the page's one row, each by OWL's label with its one-line hint on hover. */
export default function OntologyViewer({
  model,
  rawPanel,
  displayMode,
  entityType,
  selectedEntityIri,
  storageScope,
  onDisplayModeChange,
  onEntityTypeChange,
  onSelectEntity,
  renderTermDetails,
  failingIris,
}: OntologyViewerProps) {
  const { t } = useTranslation();
  const entities = useMemo(() => (entityType === "relations" ? [] : model.entitiesByType[entityType]), [entityType, model.entitiesByType]);
  const activeEntitiesByIri = useMemo(() => new Map(entities.map((entity) => [entity.iri, entity])), [entities]);
  const { entitiesByIri, entityTypeByIri } = useMemo(() => buildOntologyEntityIndexes(model), [model]);
  const relations = useMemo(() => buildOntologyRelations(model, entitiesByIri, entityTypeByIri), [entitiesByIri, entityTypeByIri, model]);
  const relationsBySource = useMemo(() => groupRelationsBySource(relations), [relations]);
  const relationsByTarget = useMemo(() => groupRelationsByTarget(relations), [relations]);
  const relationRootEntities = useMemo(
    () =>
      model.entitiesByType.classes
        .filter((entity) => !isOntologyRootIri(entity.iri))
        .sort((left, right) => {
          const leftCount = relationsBySource.get(left.iri)?.length ?? 0;
          const rightCount = relationsBySource.get(right.iri)?.length ?? 0;
          return leftCount !== rightCount ? rightCount - leftCount : entityDisplayName(left).localeCompare(entityDisplayName(right));
        }),
    [model.entitiesByType.classes, relationsBySource],
  );
  const selectedEntity =
    entityType === "relations"
      ? (selectedEntityIri ? entitiesByIri.get(selectedEntityIri) : undefined) ?? relationRootEntities[0] ?? null
      : (selectedEntityIri ? activeEntitiesByIri.get(selectedEntityIri) : undefined) ??
        topLevelEntities(entities, entityType)[0] ??
        null;
  const lookup: EntityLookup = {
    entitiesByIri,
    entityTypeByIri,
    onSelectRelatedEntity: (iri, relatedType) => {
      onSelectEntity(iri, relatedType);
    },
  };

  let details: ReactNode;
  if (entityType === "relations") {
    details = (
      <OntologyRelationDetails
        entity={selectedEntity}
        incomingRelations={selectedEntity ? (relationsByTarget.get(selectedEntity.iri) ?? []) : []}
        outgoingRelations={selectedEntity ? (relationsBySource.get(selectedEntity.iri) ?? []) : []}
        lookup={lookup}
      />
    );
  } else if (selectedEntity !== null && !isOntologyRootIri(selectedEntity.iri)) {
    details = renderTermDetails(selectedEntity, lookup);
  } else {
    details = <OntologyEntityDetails entity={selectedEntity} lookup={lookup} />;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {displayMode === "structured" ? (
          <SegmentedControl
            label={t("ontologyViewer.entityTypeChoice")}
            // OWL's own label, its plain hint on hover (V23, H-22).
            options={ONTOLOGY_VIEWER_PANES.map((pane) => ({ key: pane, label: t(ENTITY_TYPE_LABEL_KEYS[pane]), title: t(`ontologyViewer.entityTypeHints.${pane}`) }))}
            selected={entityType}
            onSelect={onEntityTypeChange}
            size="regular"
          />
        ) : (
          <span />
        )}
        <SegmentedControl
          label={t("ontologyViewer.displayMode")}
          options={(["structured", "raw"] as const).map((mode) => ({ key: mode, label: t(`ontologyViewer.${mode}`) }))}
          selected={displayMode}
          onSelect={onDisplayModeChange}
          size="regular"
        />
      </div>
      {displayMode === "raw" ? (
        rawPanel
      ) : (
        <ResizableSplitPane
          storageKey={`${storageScope}:structuredSplitWidth`}
          ariaLabel={t("ontologyViewer.resizePanels")}
          defaultLeftWidth={36}
          minLeftWidth={14}
          maxLeftWidth={80}
          minLeftPaneSize="220px"
          // On a wide screen the split fills the page's height; narrow, each pane scrolls on its own.
          className="min-h-0 flex-1 lg:grid-rows-[minmax(0,1fr)]"
          leftPaneClassName="app-code-panel max-h-[60vh] overflow-auto p-3 lg:max-h-none"
          rightPaneClassName="max-h-[60vh] overflow-auto border-t border-[var(--app-border)] pt-4 lg:max-h-none lg:border-t-0 lg:pl-4 lg:pr-2 lg:pt-0"
          left={
            entityType === "relations" ? (
              <OntologyRelationTree
                rootEntities={relationRootEntities}
                entitiesByIri={entitiesByIri}
                relationsBySource={relationsBySource}
                selectedIri={selectedEntity?.iri ?? null}
                storageKey={`${storageScope}:relations:expanded`}
                onSelect={onSelectEntity}
                t={t}
              />
            ) : (
              <OntologyHierarchyTree
                entities={entities}
                entityType={entityType}
                selectedIri={selectedEntity?.iri ?? null}
                storageKey={`${storageScope}:${entityType}:expanded`}
                onSelect={onSelectEntity}
                failingIris={failingIris}
                t={t}
              />
            )
          }
          right={details}
        />
      )}
    </div>
  );
}
